import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import MaterialIcon from "../../elements/MaterialIcon.jsx";
import GroceryListItemEdit from "./GroceryListItemEdit.jsx";
import { changeActiveGroceryList } from "../../../redux/actions/calendar.js";
import { selectRowsOfList } from "../../../redux/food/selectors.js";
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { CALENDAR_DRAG_TYPE } from './dragTypes';
import { calendarDropStyle } from './pointerDropTarget';
import BoughtListItems, { useBoughtListItemActions } from "./BoughtListItems.jsx";
import useOffline from "../../hooks/useOffline.js";

/**
 * A bought list opened in the active list's place, from the food store (`list` is its entity): a
 * record of a shop, so it only offers to put an item back on the active list.
 */
export function BoughtListDisplay({ list }) {
	const dispatch = useDispatch();
	const rows = useSelector((state) => selectRowsOfList(list.key)(state.food.entities));
	const boughtItemActions = useBoughtListItemActions();
	const items = rows.filter((row) => row.name !== '');
	const day = list.purchased_on ? moment(list.purchased_on, 'YYYY-MM-DD') : null;

	return (
		<div className='bought_list_panel'>
			<div className='bought_list_panel__header'>
				<button
					type='button'
					className='btn-flat bought_list_panel__back'
					title='Back to the grocery list'
					aria-label='Back to the grocery list'
					onClick={() => dispatch(changeActiveGroceryList(null))}
				>
					<MaterialIcon>arrow_back</MaterialIcon>
				</button>
				<div>
					<div className='bought_list_panel__name'>
						{day ? day.format('dddd, MMMM D') : list.name}
					</div>
					<div className='bought_list_panel__meta'>
						Bought · {items.length} item{items.length === 1 ? '' : 's'}
					</div>
				</div>
			</div>
			<BoughtListItems items={items} {...boughtItemActions} />
		</div>
	);
}

/**
 * The active list, which is the one that is edited: items are typed into its blank bottom row,
 * reordered by dragging, and ticked off onto the day's bought list. `groceryList` is its draft
 * (food/groceryListDraft.js), which is what is typed into and saved. Reordering saves the list, which
 * is online-only, so offline the list takes no drops; its rows can still be dragged onto the calendar.
 */
export default function GroceryListDisplay({ groceryList }) {
	const [, updateGroceryListItemEdits] = useState(0);
	const offline = useOffline();

	const groceryListItemKeyDown = (groceryListItem, event) => {
		if((event.key === 'Tab' || event.key === 'Enter')) {
			event.stopPropagation();
			event.preventDefault();

			let items = groceryList.getListableItems();
			if(event.shiftKey === true) {
				items = items.reverse();
			}
			let focusNext = false;
			for(let item of items) {
				if(focusNext) {
					if(item.inputRef?.current) {
						item.inputRef.current.focus();
						break;
					}
				}
				if(item === groceryListItem) {
					focusNext = true;
				}
			}
		}
	};

	const groceryListItemEditCallback = () => {
		updateGroceryListItemEdits((prev) => {
			return prev + 1;
		});
	};

	const groceryListItemProps = {
		groceryList: groceryList,
		groceryListItemEditCallback,
		onKeyDown: groceryListItemKeyDown,
		showCheckboxes: true,
		readOnly: false,
	};

	const items = groceryList.getListableItems();

	return (
		<React.Fragment>
			<div className='grocery_list_card'>
				<Droppable
					droppableId={`groceryListDroppable-${groceryList.id}`}
					type={CALENDAR_DRAG_TYPE}
					isDropDisabled={offline}>
					{(provided, snapshot) => (
						<div
							ref={provided.innerRef}
							{...provided.droppableProps}
						>
							{items.map((groceryListItem, index) => {
								if(groceryListItem.id) {
									return (
										<Draggable
											draggableId={`draggableGroceryListItem-${groceryListItem.idValue()}`}
											index={index}
											key={groceryListItem.id || groceryListItem.instanceNumber}
											isDragDisabled={!groceryListItem.id}
										>
											{(provided, snapshot) => {
												return (
													<div
														data-id={groceryListItem.id}
														data-name={groceryListItem.name}
														className='dragged-grocery-list-item'
														ref={provided.innerRef}
														{...provided.draggableProps}
														style={calendarDropStyle(provided.draggableProps.style, snapshot)}
													>
														<GroceryListItemEdit
															{...groceryListItemProps}
															dragHandleProps={provided.dragHandleProps}
															groceryListItem={groceryListItem}
															groceryListItemName={groceryListItem.getStateString()}
														/>
													</div>
												);
											}}
										</Draggable>
									);
								} else {
									return (
										<GroceryListItemEdit
											key={groceryListItem.id || groceryListItem.instanceNumber}
											{...groceryListItemProps}
											groceryListItem={groceryListItem}
											groceryListItemName={groceryListItem.getStateString()}
										/>
									);
								}
							})}
							{provided.placeholder}
						</div>
					)}
				</Droppable>
			</div>
		</React.Fragment>
	);
}
